/**
 * ES2022 runtime shims. Must be imported before anything else in index.tsx.
 *
 * Why this file exists: vite.config.ts and tsconfig.json both set
 * `target: "ES2020"`, which downlevels *our* syntax but does not polyfill
 * *runtime methods* and never transforms `node_modules` at all. Libraries we
 * ship call ES2022 methods directly — most importantly @mui/x-data-grid, whose
 * useGridVirtualScroller does `state.rows.dataRowIds.at(-1)` inside its resize
 * handler. On any browser below Chrome 92 / Safari 15.4 that throws and takes
 * the page down. So the real support floor was ES2022 while the build config
 * claimed ES2020.
 *
 * Observed as CM2-2HR (/products), CM2-2HQ (/concierge), CM2-2HN + CM2-2HS
 * (/images) — one user on Chrome Mobile 80 / Android 10 was fully blocked
 * across four surfaces in a single session.
 *
 * Scope: this fixes the methods we have actually seen throw. It is not a
 * blanket guarantee of old-browser support — that would be @vitejs/plugin-legacy,
 * which is a heavier call worth making only once we have measured how much
 * sub-Chrome-93 traffic actually signs up.
 */

// Array.prototype.at / String.prototype.at — ES2022, Chrome 92+, Safari 15.4+.
// Spec: relative index, negative counts from the end, out-of-range -> undefined.
function at(this: { length: number; [i: number]: unknown }, index: number) {
  const len = this.length;
  // Truncate toward zero the way ToIntegerOrInfinity does; `at()` -> index 0.
  const relative = Math.trunc(index) || 0;
  const k = relative >= 0 ? relative : len + relative;
  return k < 0 || k >= len ? undefined : this[k];
}

if (!Array.prototype.at) {
  Object.defineProperty(Array.prototype, "at", {
    value: at,
    writable: true,
    configurable: true,
  });
}

if (!String.prototype.at) {
  Object.defineProperty(String.prototype, "at", {
    value: at,
    writable: true,
    configurable: true,
  });
}

// Object.hasOwn — ES2022, Chrome 93+, Safari 15.4+.
// Cast rather than widening tsconfig `lib` to es2022: the ES2020 lib is what
// stops *our* code calling these methods unguarded, and that guard is worth
// keeping. Only shipped node_modules need the runtime shim.
const ObjectCtor = Object as ObjectConstructor & {
  hasOwn?: (target: object, key: PropertyKey) => boolean;
};

if (!ObjectCtor.hasOwn) {
  Object.defineProperty(Object, "hasOwn", {
    value: (target: object, key: PropertyKey) =>
      Object.prototype.hasOwnProperty.call(target, key),
    writable: true,
    configurable: true,
  });
}

export {};
